/**
 * Componentes básicos no estilo shadcn/ui (Tailwind + tokens de cor), sem dependências extras.
 * Diálogos usam <dialog> nativo (foco, Esc e backdrop do navegador).
 */
import { forwardRef, useEffect, useRef, useState, type ButtonHTMLAttributes, type ChangeEvent, type InputHTMLAttributes, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import clsx from 'clsx';
import { ComboSelect, SEARCH_FROM, readOptions, type ComboSelectProps } from './ComboSelect';
import { DATE_ORDER_LABEL, formatClock, formatIsoDate, parseDateText } from '@pmis/shared';
import { branding } from '../lib/branding';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-strong border-transparent shadow-sm shadow-accent/20',
  secondary: 'bg-panel text-fg border-line hover:bg-panel-2',
  ghost: 'bg-transparent text-fg border-transparent hover:bg-panel-2',
  danger: 'bg-err text-white border-transparent hover:opacity-90',
};

/** Classes do botão, também para links com cara de botão. */
export function buttonClass(variant: Variant = 'secondary', size: 'sm' | 'md' = 'md', className?: string) {
  return clsx(
    'inline-flex items-center justify-center gap-1.5 rounded-md border font-medium transition disabled:cursor-not-allowed disabled:opacity-50',
    size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-9 px-3.5 text-sm',
    VARIANTS[variant],
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' }>(
  ({ variant = 'secondary', size = 'md', className, type = 'button', ...rest }, ref) => (
    <button ref={ref} type={type} className={buttonClass(variant, size, className)} {...rest} />
  ),
);
Button.displayName = 'Button';

const INPUT = 'h-9 w-full rounded-md border border-line bg-panel px-2.5 text-sm text-fg placeholder:text-muted/60 disabled:opacity-60';

/** Campo de texto. Com type="date" ou "time", o campo da plataforma (formato da Administração › Geral). */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...rest }, ref) => {
  if (rest.type === 'date') return <DateField ref={ref} className={className} {...rest} />;
  if (rest.type === 'time') return <TimeField ref={ref} className={className} {...rest} />;
  return <input ref={ref} className={clsx(INPUT, className)} {...rest} />;
});
Input.displayName = 'Input';

/** Avisa quem usa o campo como se fosse o <input> do navegador: e.target.value com o valor no formato do banco. */
const changeEvent = (value: string, name?: string) => ({ target: { value, name }, currentTarget: { value, name } }) as unknown as ChangeEvent<HTMLInputElement>;

/**
 * Data no formato da plataforma (dd/mm/aaaa ou mm/dd/aaaa), e não no do navegador (que segue o idioma do computador e
 * mostrava mm/dd/aaaa em computadores em inglês). O valor continua AAAA-MM-DD. Digitando só números, as barras entram
 * sozinhas; o botão abre o calendário do navegador.
 */
const DateField = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ value, onChange, min, max, className, disabled, name, placeholder, type: _type, ...rest }, ref) => {
  const order = branding().formats.date;
  const iso = typeof value === 'string' ? value : '';
  const [text, setText] = useState(() => (iso ? formatIsoDate(iso, order) : ''));
  const [bad, setBad] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => { setText(iso ? formatIsoDate(iso, order) : ''); setBad(false); }, [iso, order]);
  const emit = (v: string) => onChange?.(changeEvent(v, name));
  const commit = (t: string) => {
    if (!t.trim()) { setBad(false); if (iso) emit(''); return; }
    const parsed = parseDateText(t, order);
    if (!parsed || (typeof min === 'string' && min && parsed < min) || (typeof max === 'string' && max && parsed > max)) { setBad(true); return; }
    setBad(false);
    if (parsed !== iso) emit(parsed); else setText(formatIsoDate(parsed, order));
  };
  const type = (t: string) => {
    const digits = t.replace(/\D/g, '').slice(0, 8);
    const auto = !t.includes('/') && /^\d*$/.test(t) ? [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join('/') : t;
    setText(auto);
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(auto)) commit(auto);
  };
  return (
    <div className="relative">
      <input ref={ref} {...rest} name={name} type="text" inputMode="numeric" autoComplete="off" value={text} disabled={disabled}
        placeholder={placeholder ?? DATE_ORDER_LABEL[order]} aria-invalid={bad || undefined} title={bad ? `Data inválida: use ${DATE_ORDER_LABEL[order]}` : undefined}
        className={clsx(INPUT, 'pr-9', bad && 'border-err! ring-1 ring-err/40', className)} onChange={(e) => type(e.target.value)} onBlur={(e) => commit(e.target.value)} />
      <button type="button" tabIndex={-1} disabled={disabled} aria-label="Abrir o calendário" title="Calendário"
        className="absolute inset-y-0 right-0 grid w-8 place-items-center text-muted hover:text-fg disabled:opacity-40"
        onClick={() => { try { picker.current?.showPicker(); } catch { picker.current?.click(); } }}>
        <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden><rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 9.5h17M8 3v4M16 3v4" /></svg>
      </button>
      <input ref={picker} type="date" tabIndex={-1} aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 size-px opacity-0"
        value={iso} min={min} max={max} onChange={(e) => emit(e.target.value)} />
    </div>
  );
});
DateField.displayName = 'DateField';

/** Hora no formato da plataforma (14:30 ou 2:30 PM); o valor continua HH:mm. */
const TimeField = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ value, onChange, className, name, placeholder, type: _type, ...rest }, ref) => {
  const fmt = branding().formats.time;
  const hhmm = typeof value === 'string' ? value.slice(0, 5) : '';
  const [text, setText] = useState(() => (hhmm ? formatClock(hhmm, fmt) : ''));
  const [bad, setBad] = useState(false);
  useEffect(() => { setText(hhmm ? formatClock(hhmm, fmt) : ''); setBad(false); }, [hhmm, fmt]);
  const commit = (t: string) => {
    if (!t.trim()) { setBad(false); if (hhmm) onChange?.(changeEvent('', name)); return; }
    const m = /^\s*(\d{1,2})(?::?(\d{2}))?\s*(am|pm)?\s*$/i.exec(t);
    let h = m ? Number(m[1]) : NaN;
    const min = m?.[2] ? Number(m[2]) : 0;
    if (m?.[3]) h = (h % 12) + (m[3].toLowerCase() === 'pm' ? 12 : 0);
    if (!m || h > 23 || min > 59) { setBad(true); return; }
    setBad(false);
    const v = `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
    setText(formatClock(v, fmt));
    if (v !== hhmm) onChange?.(changeEvent(v, name));
  };
  return <input ref={ref} {...rest} name={name} type="text" autoComplete="off" value={text} placeholder={placeholder ?? (fmt === '12h' ? '2:30 PM' : '14:30')}
    aria-invalid={bad || undefined} className={clsx(INPUT, bad && 'border-err! ring-1 ring-err/40', className)} onChange={(e) => setText(e.target.value)} onBlur={(e) => commit(e.target.value)} />;
});
TimeField.displayName = 'TimeField';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...rest }, ref) => (
  <textarea
    ref={ref}
    className={clsx('w-full rounded-md border border-line bg-panel px-2.5 py-2 text-sm text-fg placeholder:text-muted/60 disabled:opacity-60', className)}
    {...rest}
  />
));
Textarea.displayName = 'Textarea';

/**
 * Lista suspensa. Com mais de SEARCH_FROM opções (projetos, pessoas, clientes, categorias…), vira a lista com busca: digite
 * parte do nome e a lista mostra só o que bate (item 38). `searchable` força um ou outro.
 */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { searchable?: boolean }>(({ className, searchable, children, ...rest }, ref) => {
  const items = readOptions(children);
  if (searchable ?? items.filter((o) => o.value !== '').length > SEARCH_FROM) {
    const { value, onChange, disabled, required, id, name, title, onClick } = rest;
    return <ComboSelect items={items} value={value} onChange={onChange} disabled={disabled} required={required} id={id} name={name} title={title} className={className}
      onClick={onClick as unknown as ComboSelectProps['onClick']} aria-label={rest['aria-label']} aria-invalid={rest['aria-invalid'] as ComboSelectProps['aria-invalid']} />;
  }
  return (
    <select ref={ref} className={clsx('h-9 w-full rounded-md border border-line bg-panel px-2 text-sm text-fg disabled:opacity-60', className)} {...rest}>{children}</select>
  );
});
Select.displayName = 'Select';

/** Campo com rótulo. `required`: asterisco vermelho no rótulo (e "obrigatório" para o leitor de tela). */
export function Field({ label, hint, children, className, required }: { label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string; required?: boolean }) {
  return (
    <label className={clsx('flex flex-col gap-1', className)}>
      <span className="text-xs font-medium text-muted">{label}{required ? <><span className="ml-0.5 font-bold text-err" aria-hidden="true">*</span><span className="sr-only"> (obrigatório)</span></> : null}</span>
      {children}
      {hint ? <span className="text-xs text-muted">{hint}</span> : null}
    </label>
  );
}

export function Card({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={clsx('rounded-xl border border-line bg-panel shadow-sm', className)}>
      {title || actions ? (
        <header className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
          <h3 className="text-sm font-semibold">{title}</h3>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
      ) : null}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Badge({ tone = 'neutral', children, className, title }: { tone?: 'neutral' | 'accent' | 'ok' | 'warn' | 'err'; children: ReactNode; className?: string; title?: string }) {
  const tones = {
    neutral: 'bg-panel-2 text-muted border-line',
    accent: 'bg-panel-2 text-accent border-accent/40',
    ok: 'bg-panel-2 text-ok border-ok/40',
    warn: 'bg-warn-bg text-warn border-warn/40',
    err: 'bg-err-bg text-err border-err/40',
  };
  return <span title={title} className={clsx('inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium', tones[tone], className)}>{children}</span>;
}

export function Spinner({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-2 text-sm text-muted">
      <span className="size-4 animate-spin rounded-full border-2 border-line border-t-accent" />
      {label}
    </div>
  );
}

export function Alert({ tone = 'warn', children, className }: { tone?: 'warn' | 'err' | 'ok'; children: ReactNode; className?: string }) {
  const tones = { warn: 'bg-warn-bg text-warn border-warn/40', err: 'bg-err-bg text-err border-err/40', ok: 'bg-panel-2 text-ok border-ok/40' };
  return <div role={tone === 'err' ? 'alert' : 'status'} className={clsx('rounded-md border px-3 py-2 text-sm', tones[tone], className)}>{children}</div>;
}

export function Dialog({ open, onClose, title, children, footer, wide }: {
  open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // o showModal põe o foco no primeiro botão (o ✕); o campo marcado com data-autofocus fica com o cursor
      d.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    }
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      className={clsx('m-auto max-h-[90vh] w-[calc(100%-32px)] rounded-lg border border-line bg-panel p-0 text-fg shadow-xl', wide ? 'max-w-5xl' : 'max-w-lg')}
      aria-label={title}
    >
      {open ? (
        <div className="flex max-h-[90vh] flex-col">
          <header className="flex items-center justify-between border-b border-line px-5 py-3">
            <h2 className="text-base font-semibold">{title}</h2>
            <button type="button" onClick={onClose} className="rounded p-1 text-muted hover:bg-panel-2" aria-label="Fechar">✕</button>
          </header>
          <div className="overflow-auto px-5 py-4">{children}</div>
          {footer ? <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</footer> : null}
        </div>
      ) : null}
    </dialog>
  );
}

/** Abas; quando não cabem numa linha, quebram para a seguinte (todas continuam à vista, sem rolagem lateral). */
export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: ReactNode }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div role="tablist" className="flex flex-wrap gap-x-1 border-b border-line">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          type="button"
          aria-selected={value === t.id}
          onClick={() => onChange(t.id)}
          className={clsx(
            '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium',
            value === t.id ? 'border-accent text-fg' : 'border-transparent text-muted hover:text-fg',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Indicador rotulado: rótulo, valor e uma linha de detalhe, alinhados à esquerda. Com `onClick`, o valor vira link. */
export function Stat({ label, value, tone, hint, sub, onClick, className }: {
  label: string; value: ReactNode; tone?: 'ok' | 'warn' | 'err'; hint?: string; sub?: ReactNode; onClick?: () => void; className?: string;
}) {
  const valueClass = clsx('block max-w-full truncate text-left text-base font-semibold tabular-nums',
    tone === 'err' && 'text-err', tone === 'warn' && 'text-warn', tone === 'ok' && 'text-ok');
  return (
    <div className={clsx('min-w-0', className)} title={hint}>
      <div className="truncate text-xs text-muted">{label}</div>
      {onClick
        ? <button type="button" onClick={onClick} className={clsx(valueClass, 'underline-offset-2 hover:underline')}>{value}</button>
        : <div className={valueClass}>{value}</div>}
      {sub ? <div className="truncate text-xs text-muted">{sub}</div> : null}
    </div>
  );
}

/**
 * Ícone "i" com uma explicação: aparece ao passar o mouse ou focar e fica aberta ao clicar (fecha com Esc ou clicando
 * fora). Substitui os quadros de texto no topo das telas.
 */
export function InfoTip({ children, label = 'Sobre esta área', className }: { children: ReactNode; label?: string; className?: string }) {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!pinned) return;
    const out = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setPinned(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setPinned(false); };
    document.addEventListener('mousedown', out);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc); };
  }, [pinned]);
  const open = hover || pinned;
  return (
    <span ref={ref} className={clsx('relative inline-flex align-middle', className)} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button type="button" aria-label={label} aria-expanded={open} onClick={() => setPinned((v) => !v)} onFocus={() => setHover(true)} onBlur={() => setHover(false)}
        className={clsx('grid size-5 place-items-center rounded-full border text-[11px] font-bold italic leading-none transition',
          open ? 'border-accent bg-accent text-accent-fg' : 'border-line text-muted hover:border-accent hover:text-accent')} data-testid="info-tip">
        i
      </button>
      {open ? (
        <span role="tooltip" className="absolute left-0 top-7 z-30 w-[min(440px,80vw)] rounded-md border border-line bg-panel p-3 text-left text-sm font-normal not-italic leading-relaxed text-fg shadow-lg">
          {children}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Logo da empresa: o de fundo claro e o de fundo escuro (segue o tema do sistema). Sem logo enviado em Administração ›
 * Geral, o da AD Digital (azul-marinho no claro, branco no escuro).
 */
export function Logo({ className, onDark }: { className?: string; onDark?: boolean }) {
  const { light, dark } = branding().logo;
  const onLight = light ?? '/brand/logo-ad-digital.png';
  const onDarkSrc = dark ?? '/brand/logo-ad-digital-branco.png';
  if (onDark) return <img src={onDarkSrc} alt="Logo da empresa" className={clsx('w-auto', className)} />;
  return (
    <picture>
      <source srcSet={onDarkSrc} media="(prefers-color-scheme: dark)" />
      <img src={onLight} alt="Logo da empresa" className={clsx('w-auto', className)} />
    </picture>
  );
}

/**
 * Barra de rolagem para os lados presa ao pé da tela, sincronizada com a tabela `el` (que esconde a dela, com a classe
 * hide-hbar). Fica logo abaixo da tabela quando o fim dela está à vista e gruda no pé da tela quando não está; só
 * aparece se a tabela for mais larga que o espaço. `bottom` afasta a barra de algo preso no pé (ex.: a barra de salvar).
 */
export function StickyHScroll({ el, bottom = 0 }: { el: HTMLElement | null; bottom?: number }) {
  const bar = useRef<HTMLDivElement>(null);
  // Mesma largura visível da tabela (sem a barra vertical dela): as duas chegam juntas ao fim.
  const [dims, setDims] = useState({ scroll: 0, client: 0 });
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!el) return;
    const update = () => { setDims({ scroll: el.scrollWidth, client: el.clientWidth }); setShow(el.scrollWidth > el.clientWidth + 1); if (bar.current) bar.current.scrollLeft = el.scrollLeft; };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    const follow = () => { if (bar.current && Math.abs(bar.current.scrollLeft - el.scrollLeft) > 1) bar.current.scrollLeft = el.scrollLeft; };
    el.addEventListener('scroll', follow, { passive: true });
    return () => { ro.disconnect(); el.removeEventListener('scroll', follow); };
  }, [el]);
  if (!show) return null;
  return (
    <div ref={bar} aria-hidden className="sticky z-20 h-4 overflow-x-auto overflow-y-hidden rounded-b-lg border-t border-line bg-panel/95 backdrop-blur" style={{ bottom, width: dims.client || undefined }}
      onScroll={(e) => { const x = e.currentTarget.scrollLeft; if (el && Math.abs(el.scrollLeft - x) > 1) el.scrollLeft = x; }}>
      <div style={{ width: dims.scroll, height: 1 }} />
    </div>
  );
}
